import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { FlagImage, parseCountry } from "react-international-phone";
import { getOrderedCountries } from "./countrySearch";
import "./SearchableCountrySelector.css";

function safeParseCountry(entry) {
  if (!entry) return null;
  try {
    return parseCountry(entry);
  } catch {
    return null;
  }
}

function HighlightMatch({ text, query }) {  if (!query?.trim()) {
    return text;
  }

  const normalizedText = text.toLowerCase();
  const normalizedQuery = query.trim().toLowerCase();
  const matchIndex = normalizedText.indexOf(normalizedQuery);

  if (matchIndex === -1) {
    return text;
  }

  return (
    <>
      {text.slice(0, matchIndex)}
      <mark className="sac-country-search__highlight">
        {text.slice(matchIndex, matchIndex + normalizedQuery.length)}
      </mark>
      {text.slice(matchIndex + normalizedQuery.length)}
    </>
  );
}

function SearchableCountrySelector({
  countries,
  preferredCountries = ["in", "ae", "us", "gb"],
  selectedCountry,
  onSelect,
  disabled = false,
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [focusedIndex, setFocusedIndex] = useState(0);
  const containerRef = useRef(null);
  const searchInputRef = useRef(null);
  const listRef = useRef(null);

  const selected = useMemo(
    () =>
      countries
        .map(parseCountry)
        .find((country) => country.iso2 === selectedCountry?.toLowerCase()),
    [countries, selectedCountry],
  );

  const visibleCountries = useMemo(
    () => getOrderedCountries(countries, preferredCountries, searchQuery),
    [countries, preferredCountries, searchQuery],
  );

  const preferredSet = useMemo(
    () => new Set(preferredCountries.map((iso2) => iso2.toLowerCase())),
    [preferredCountries],
  );

  const closeDropdown = useCallback(() => {
    setIsOpen(false);
    setSearchQuery("");
  }, []);

  const toggleDropdown = () => {
    if (disabled) return;
    setIsOpen((open) => {
      if (open) {
        setSearchQuery("");
      }
      return !open;
    });
  };

  useEffect(() => {
    if (!isOpen) return undefined;

    const frame = window.requestAnimationFrame(() => {
      searchInputRef.current?.focus();
    });

    return () => window.cancelAnimationFrame(frame);
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return undefined;

    const handlePointerDown = (event) => {
      if (!containerRef.current?.contains(event.target)) {
        closeDropdown();
      }
    };

    document.addEventListener("mousedown", handlePointerDown);
    return () => document.removeEventListener("mousedown", handlePointerDown);
  }, [isOpen, closeDropdown]);

  useEffect(() => {
    if (!visibleCountries.length) {
      setFocusedIndex(-1);
      return;
    }

    const selectedIndex = visibleCountries.findIndex(
      (entry) => safeParseCountry(entry)?.iso2 === selectedCountry?.toLowerCase(),
    );
    setFocusedIndex((current) => {
      const nextIndex = selectedIndex >= 0 ? selectedIndex : 0;
      if (current >= 0 && current < visibleCountries.length) {
        return current;
      }
      return nextIndex;
    });
  }, [searchQuery, selectedCountry, visibleCountries]);

  useEffect(() => {
    if (!isOpen || !listRef.current || !visibleCountries.length || focusedIndex < 0) {
      return;
    }

    const focusedCountry = safeParseCountry(visibleCountries[focusedIndex]);
    if (!focusedCountry?.iso2) return;

    const focusedItem = listRef.current.querySelector(
      `[data-country-option="${focusedCountry.iso2}"]`,
    );
    if (typeof focusedItem?.scrollIntoView === "function") {
      focusedItem.scrollIntoView({ block: "nearest" });
    }
  }, [focusedIndex, isOpen, visibleCountries]);
  const handleSelect = (country) => {
    onSelect?.(country);
    closeDropdown();
  };

  const handleSearchKeyDown = (event) => {
    if (!visibleCountries.length) {
      if (event.key === "Escape") {
        event.preventDefault();
        closeDropdown();
      }
      return;
    }

    if (event.key === "ArrowDown") {
      event.preventDefault();
      setFocusedIndex((current) =>
        Math.min(current + 1, Math.max(visibleCountries.length - 1, 0)),
      );
      return;
    }

    if (event.key === "ArrowUp") {
      event.preventDefault();
      setFocusedIndex((current) => Math.max(current - 1, 0));
      return;
    }

    if (event.key === "Enter") {
      event.preventDefault();
      const country = safeParseCountry(visibleCountries[focusedIndex]);
      if (country) handleSelect(country);
      return;
    }
    if (event.key === "Escape") {
      event.preventDefault();
      closeDropdown();
    }
  };

  return (
    <div className="sac-country-search" ref={containerRef}>
      <button
        type="button"
        className="sac-country-search__trigger"
        onClick={toggleDropdown}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-label={`Selected country: ${selected?.name || "Select country"}`}
      >
        <FlagImage iso2={selected?.iso2} className="sac-country-search__flag" />
        <span className="sac-country-search__arrow" aria-hidden="true" />
      </button>

      {isOpen && (
        <div className="sac-country-search__dropdown">
          <div className="sac-country-search__search-wrap">
            <input
              ref={searchInputRef}
              type="search"
              inputMode="search"
              autoComplete="off"
              autoCorrect="off"
              spellCheck={false}
              value={searchQuery}
              onChange={(event) => setSearchQuery(event.target.value)}
              onKeyDown={(event) => {
                event.stopPropagation();
                handleSearchKeyDown(event);
              }}
              onKeyUp={(event) => event.stopPropagation()}
              onClick={(event) => event.stopPropagation()}
              onMouseDown={(event) => event.stopPropagation()}              placeholder="Search country or code"
              className="sac-country-search__search"
              aria-label="Search countries"
            />
          </div>

          <ul
            ref={listRef}
            className="sac-country-search__list"
            role="listbox"
            aria-label="Countries"
          >
            {visibleCountries.length === 0 && (
              <li className="sac-country-search__empty">No countries found</li>
            )}

            {visibleCountries.map((entry, index) => {
              const country = safeParseCountry(entry);
              if (!country) return null;              const isSelected = country.iso2 === selectedCountry?.toLowerCase();
              const isFocused = index === focusedIndex;
              const isPreferred = preferredSet.has(country.iso2);
              const showDivider =
                !searchQuery &&
                preferredCountries.length > 0 &&
                isPreferred &&
                index === preferredCountries.length - 1 &&
                index < visibleCountries.length - 1;

              return (
                <li key={country.iso2}>
                  <button
                    type="button"
                    role="option"
                    aria-selected={isSelected}
                    data-country-option={country.iso2}
                    className={[
                      "sac-country-search__option",
                      isSelected && "sac-country-search__option--selected",
                      isFocused && "sac-country-search__option--focused",
                      isPreferred && "sac-country-search__option--preferred",
                    ]
                      .filter(Boolean)
                      .join(" ")}
                    onMouseEnter={() => setFocusedIndex(index)}
                    onClick={() => handleSelect(country)}
                  >
                    <FlagImage
                      iso2={country.iso2}
                      className="sac-country-search__option-flag"
                    />
                    <span className="sac-country-search__option-name">
                      <HighlightMatch text={country.name} query={searchQuery} />
                    </span>
                    <span className="sac-country-search__option-code">
                      +{country.dialCode}
                    </span>
                  </button>
                  {showDivider && (
                    <hr className="sac-country-search__divider" aria-hidden="true" />
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
}

export default SearchableCountrySelector;
